import { createFeatureSelector, createSelector } from '@ngrx/store';
import { paymentFeatureKey, PaymentState, transactionListAdapter } from '@store/payment/payment.reducer';
import { SelectOption } from '@core/models';
import { PAYMENT_WITHDRAW_ADDRESS_ASYNC_ERROR_KEY } from '@data/const';
import { UserTransactionsViewFilter } from '@store/user';

const selectPaymentFeature = createFeatureSelector<PaymentState>(paymentFeatureKey);

const selectPaymentState = createSelector(
  selectPaymentFeature,
  (state) => state.payment,
);
const selectTransactionState = createSelector(
  selectPaymentFeature,
  (state) => state.transactions,
);

const selectChargeForm = createSelector(
  selectPaymentFeature,
  (state) => state.chargeForm,
);

const selectChargeSelectedCurrencyValue = createSelector(
  selectChargeForm,
  (form) => form.controls.currency.value,
);

const selectChargeSelectedPaymentTypeValue = createSelector(
  selectChargeForm,
  (form) => form.controls.paymentType.value,
);

const selectChargeAmountValue = createSelector(
  selectChargeForm,
  (form) => form.controls.amount.value,
);


const selectCurrencyList = createSelector(
  selectPaymentFeature,
  (state) => state.currencies,
);

const selectCurrencySelectOptionsList = createSelector(
  selectCurrencyList,
  (currencyList) => {
    return currencyList
      .map(currency => ({
        label: `${currency.name}`,
        value: currency.name,
      } as SelectOption));
  },
);

const selectChargeCurrencyButtonValues = createSelector(
  selectCurrencyList,
  selectChargeSelectedCurrencyValue,
  (currencyList, selectedCurrency) => {
    return currencyList.find(currency => currency.iso_code === selectedCurrency)?.default_sum ?? [];
  },
);

const selectChargeCurrencyExchangeRate = createSelector(
  selectCurrencyList,
  selectChargeSelectedCurrencyValue,
  (currencyList, selectedCurrency) => {
    return currencyList.find(currency => currency.iso_code === selectedCurrency)?.exchange_rate ?? 1;
  },
);

const selectChargePaymentTypeList = createSelector(
  selectPaymentFeature,
  (state) => state.chargePaymentTypes,
);

const selectChargePaymentTypesSelectOptionsList = createSelector(
  selectChargePaymentTypeList,
  selectChargeSelectedCurrencyValue,
  selectChargeAmountValue,
  (paymentTypeList, selectedCurrency, amount) => {
    return paymentTypeList
      .filter(paymentType => paymentType.iso_codes.find(code => code === selectedCurrency))
      .filter(paymentType => !!amount && (amount >= paymentType.min && amount <= paymentType.max))
      .map(paymentType => ({
        label: paymentType.name,
        value: paymentType.type,
        icon: paymentType.logo ?? paymentType.type,
      } as SelectOption));
  },
);

const selectChargePaymentPayServiceFee = createSelector(
  selectChargePaymentTypeList,
  selectChargeSelectedPaymentTypeValue,
  (paymentTypeList, selectedPaymentType) => {
    return paymentTypeList.find(paymentType => paymentType.type === selectedPaymentType)?.payservice_fee ?? 0;
  },
);

const selectChargePaymentLimits = createSelector(
  selectChargePaymentTypeList,
  selectChargeSelectedPaymentTypeValue,
  (paymentTypeList, selectedPaymentType) => {
    const paymentType = paymentTypeList.find(paymentTypeItem => paymentTypeItem.type === selectedPaymentType);
    return {
      min: paymentType?.min ?? 0,
      max: paymentType?.max ?? 99999,
    };
  },
);


const selectResultChargeAmount = createSelector(
  selectChargeAmountValue,
  selectChargePaymentPayServiceFee,
  selectChargeCurrencyExchangeRate,
  (amount, fee, exchangeRate) => {
    return (amount / exchangeRate) * (100 - fee) / 100;
  },
);


const selectWithdrawForm = createSelector(
  selectPaymentFeature,
  (state) => state.withdrawForm,
);

const selectWithdrawSelectedCurrencyValue = createSelector(
  selectWithdrawForm,
  (form) => form.controls.currency.value,
);

const selectWithdrawAmountValue = createSelector(
  selectWithdrawForm,
  (form) => form.controls.amount.value,
);

const selectWithdrawPaymentTypeValue = createSelector(
  selectWithdrawForm,
  (form) => form.controls.paymentType.value,
);


const selectWithdrawPaymentTypeList = createSelector(
  selectPaymentFeature,
  (state) => state.withdrawPaymentTypes,
);

const selectWithdrawCurrentPaymentType = createSelector(
  selectWithdrawPaymentTypeList,
  selectWithdrawPaymentTypeValue,
  (paymentTypeList, currentType) =>
    paymentTypeList.find(paymentType => paymentType.type === currentType),
);

const selectWithdrawAddressIsCreditCard = createSelector(
  selectWithdrawCurrentPaymentType,
  (currentType) => {
    return currentType?.adr_type === 'CARD';
  },
);

const selectWithdrawPaymentLimits = createSelector(
  selectWithdrawCurrentPaymentType,
  (currentType) => {
    return {
      min: currentType?.min ?? 0,
      max: currentType?.max ?? 99999,
    };
  },
);

const selectWithdrawCurrencyExchangeRate = createSelector(
  selectCurrencyList,
  selectWithdrawSelectedCurrencyValue,
  (currencyList, selectedCurrency) => {
    return currencyList.find(currency => currency.iso_code === selectedCurrency)?.exchange_rate ?? 1;
  },
);

const selectWithdrawPaymentPayServiceFee = createSelector(
  selectWithdrawCurrentPaymentType,
  (currentType) => currentType?.payservice_fee ?? 0,
);

const selectWithdrawPaymentServiceFee = createSelector(
  selectWithdrawCurrentPaymentType,
  (currentType) => currentType?.service_fee ?? 0,
);

const selectWithdrawPaymentTypeSelectOptionsList = createSelector(
  selectWithdrawPaymentTypeList,
  selectWithdrawSelectedCurrencyValue,
  selectWithdrawAmountValue,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (paymentTypeList, selectedCurrency, amount) => {
    return paymentTypeList
      // .filter(paymentType => paymentType.name !== selectedCurrency)
      // .filter(paymentType => !!amount || (amount >= paymentType.min && amount <= paymentType.max));
      .map(paymentType => ({
        label: paymentType.name,
        value: paymentType.type,
        icon: paymentType.type,
      } as SelectOption));
  },
);

const selectHasWithdrawAddressErrors = createSelector(
  selectWithdrawForm,
  (form) => {
    return Object.keys(form.controls.address.errors).length;
  },
);

const selectHasWithdrawAddressAsyncError = createSelector(
  selectWithdrawForm,
  (form) => {
    return Object.keys(form.controls.address.errors).some(error => error.includes(PAYMENT_WITHDRAW_ADDRESS_ASYNC_ERROR_KEY));
  },
);

const selectResultWithdrawAmount = createSelector(
  selectWithdrawAmountValue,
  selectWithdrawPaymentPayServiceFee,
  selectWithdrawPaymentServiceFee,
  selectWithdrawCurrencyExchangeRate,
  (amount, payServiceFee, fee, exchangeRate) => {
    return amount * exchangeRate * (100 - fee - payServiceFee) / 100;
  },
);

const {
  selectAll: selectAllTransactions,
  selectEntities: selectAllTransactionEntities,
} = transactionListAdapter.getSelectors();

const selectTransactionsState = createSelector(
  selectPaymentFeature,
  (feature) => feature.transactions,
);
const selectTransactionList = createSelector(selectTransactionsState, selectAllTransactions);
const selectTransactionEntities = createSelector(selectTransactionsState, selectAllTransactionEntities);

const selectTransactionCurrentView = createSelector(
  selectTransactionsState,
  (transactionState) => transactionState.currentView,
);

const selectTransactions = createSelector(
  selectTransactionList,
  selectTransactionCurrentView,
  (transactionList, currentView) => transactionList
    .filter((transaction) =>
      currentView === UserTransactionsViewFilter.ALL || transaction.main_type === currentView),
);

const selectTransactionsCount = createSelector(
  selectTransactions,
  (transactions) => transactions.length,
);

const selectTransactionsNextPage = createSelector(
  selectTransactionsState,
  (transactions) => transactions.nextPage,
);
const selectTransactionsIsLoading = createSelector(
  selectTransactionsState,
  (transactions) => transactions.isLoading,
);

const selectIsLoading = createSelector(
  selectPaymentFeature,
  (state) => state.isLoading,
);

const selectPaymentMode = createSelector(
  selectPaymentFeature,
  (state) => state.paymentMode,
);

const selectPaymentStatus = createSelector(
  selectPaymentFeature,
  (state) => state.paymentStatus,
);

const selectWalletAddress = createSelector(
  selectPaymentFeature,
  (state) => state.walletAddress,
);
const selectWalletAmount = createSelector(
  selectPaymentFeature,
  (state) => state.walletAmount,
);
const selectInvoiceId = createSelector(
  selectPaymentFeature,
  (state) => state.invoiceId,
);
const selectInvoice = createSelector(
  selectPaymentFeature,
  (state) => state.invoice,
);
const selectProviders = createSelector(
  selectPaymentFeature,
  (state) => state.chargePaymentTypes,
);

export const paymentSelectors = {
  selectIsLoading,
  selectPaymentFeature,
  selectTransactionsCount,
  selectTransactionCurrentView,
  selectHasWithdrawAddressAsyncError,
  selectResultWithdrawAmount,
  selectTransactionEntities,
  selectTransactionsNextPage,
  selectTransactionsIsLoading,
  selectPaymentState,
  selectTransactionState,
  selectCurrencySelectOptionsList,
  selectChargeCurrencyButtonValues,
  selectChargePaymentTypesSelectOptionsList,
  selectChargePaymentLimits,
  selectResultChargeAmount,
  selectWithdrawAddressIsCreditCard,
  selectWithdrawPaymentLimits,
  selectWithdrawPaymentTypeSelectOptionsList,
  selectHasWithdrawAddressErrors,
  selectWithdrawForm,
  selectChargeForm,
  selectChargePaymentTypeList,
  selectWithdrawPaymentTypeList,
  selectCurrencyList,
  selectTransactions,
  selectWithdrawSelectedCurrencyValue,
  selectWithdrawCurrencyExchangeRate,
  selectWithdrawPaymentPayServiceFee,
  selectWithdrawPaymentServiceFee,
  selectChargeSelectedCurrencyValue,
  selectChargeCurrencyExchangeRate,
  selectChargePaymentPayServiceFee,
  selectPaymentMode,
  selectPaymentStatus,
  selectWalletAddress,
  selectWalletAmount,
  selectInvoiceId,
  selectInvoice,
  selectProviders,
};
